/**
 * usePresence — Sends periodic heartbeats to the presence API
 * to indicate the user is active on a project page.
 */

import { useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { usePresenceStore } from "@/stores/presence";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const HEARTBEAT_INTERVAL = 30_000; // 30 seconds

async function sendHeartbeat(projectId: string, page: string): Promise<void> {
  try {
    const token = localStorage.getItem("ftl_access_token");
    if (!token) return;

    const res = await fetch(`${API_BASE}/presence/heartbeat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ project_id: projectId, page }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.active_users) {
        usePresenceStore.getState().setActiveUsers(data.active_users);
      }
    }
  } catch {
    // Silently fail — presence is best-effort
  }
}

async function sendLeave(projectId: string): Promise<void> {
  try {
    const token = localStorage.getItem("ftl_access_token");
    if (!token) return;

    // Use sendBeacon for reliability on page unload, fall back to fetch
    const body = JSON.stringify({ project_id: projectId, page: "" });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API_BASE}/presence/leave`, blob);
    } else {
      await fetch(`${API_BASE}/presence/leave`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: true,
      });
    }
  } catch {
    // Best-effort
  }
}

export function usePresence(projectId: string | undefined) {
  const { pathname } = useLocation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearPresence = usePresenceStore((s) => s.clearPresence);

  // Extract page name from pathname
  const page = pathname.split("/").pop() ?? "";

  useEffect(() => {
    if (!projectId) return;

    // Send initial heartbeat
    sendHeartbeat(projectId, page);

    // Set up periodic heartbeat
    intervalRef.current = setInterval(() => {
      sendHeartbeat(projectId, page);
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Signal departure
      sendLeave(projectId);
      clearPresence();
    };
  }, [projectId, page, clearPresence]);
}
